// src/pages/BindleRequest.jsx
// Bindle Access Request — 3-stage approval (Bindle POC → Leadership → SPS Team)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lookupManager, submitToApprovals, checkLeaderLevel } from '../services/api';
import { normalizeAlias } from '../services/alias';

const BindleRequest = ({ userAlias }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Form, 2: Leader, 3: Review, 4: Submitting, 5: Done
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    // Form fields
    const [requestType, setRequestType] = useState('');
    const [permissionType, setPermissionType] = useState('');
    const [useCase, setUseCase] = useState('');
    const [paragonActions, setParagonActions] = useState('');
    const [posixAccount, setPosixAccount] = useState('');
    const [saferAICheck, setSaferAICheck] = useState('');
    const [saferAIId, setSaferAIId] = useState('');

    // Leader
    const [leaderAlias, setLeaderAlias] = useState('');
    const [leaderInfo, setLeaderInfo] = useState(null);
    const [isLoadingLeader, setIsLoadingLeader] = useState(false);
    const [leaderLookupError, setLeaderLookupError] = useState('');
    // True when leaderAlias came from auto-detect (trusted). Cleared when the user edits it manually.
    const [leaderAutoDetected, setLeaderAutoDetected] = useState(false);

    // Auto-detect leader on mount
    useEffect(() => {
        if (userAlias) {
            detectLeader();
        }
    }, [userAlias]);

    const detectLeader = async () => {
        setIsLoadingLeader(true);
        setLeaderLookupError('');
        try {
            // Bindle needs an L7 leader (Stage 2 = Leadership).
            const result = await lookupManager(userAlias, 7);
            // Backend returns the leader under result.l8Manager.alias (not managerAlias).
            if (result && result.l8Manager && result.l8Manager.alias) {
                setLeaderAlias(result.l8Manager.alias);
                setLeaderInfo(result.l8Manager);
                setLeaderAutoDetected(true);
            } else if (result && Array.isArray(result.managerChain)) {
                // No direct L7+ match returned — pick the closest manager at level >= 7 from the chain.
                const match = result.managerChain.find(m => m.jobLevel && m.jobLevel >= 7);
                if (match) {
                    setLeaderAlias(match.alias);
                    setLeaderInfo(match);
                    setLeaderAutoDetected(true);
                } else {
                    setLeaderLookupError('Could not auto-detect an L7+ leader. Please enter your L7 manager alias manually.');
                }
            } else {
                setLeaderLookupError('Could not auto-detect your leader. Please enter your L7 manager alias manually.');
            }
        } catch (err) {
            console.warn('Could not auto-detect leader:', err);
            setLeaderLookupError('Auto-detect failed. Please enter your L7 manager alias manually.');
        }
        setIsLoadingLeader(false);
    };

    const validateForm = () => {
        if (!requestType) return 'Please select the request type (AI Agent/System Account/Application)';
        if (!permissionType) return 'Please select the permission type (View/Update)';
        if (!useCase.trim()) return 'Please provide use case details';
        if (!paragonActions.trim()) return 'Please specify the paragon actions';
        if (!posixAccount.trim()) return 'Please provide the POSIX account name';
        if (!saferAICheck) return 'Please indicate if Safer AI check has been done';
        if (saferAICheck === 'Yes' && !saferAIId.trim()) return 'Please provide the Safer AI unique ID';
        return null;
    };

    const handleNext = async () => {
        if (step === 1) {
            const err = validateForm();
            if (err) { setError(err); return; }
            setError('');
            setStep(2);
        } else if (step === 2) {
            const alias = normalizeAlias(leaderAlias);
            setLeaderAlias(alias);   // show the bare login that is checked and submitted
            if (!alias) { setError('Please enter or confirm your L7 manager alias'); return; }
            // Auto-detected leaders are trusted. Only manually-entered/edited aliases are level-checked.
            if (!leaderAutoDetected) {
                setError('');
                setIsLoadingLeader(true);
                const res = await checkLeaderLevel(alias, 7, userAlias); // Bindle Stage 2 = L7 Leadership
                setIsLoadingLeader(false);
                if (res.status === 'self') {
                    setError('❌ You cannot select yourself as your approval leader. Please enter a different leader at or above the required level.');
                    return;
                }
                if (res.status === 'too_low') {
                    setError(`❌ Leader submitted by you is below the required level. Bindle requires an L7+ leader, but ${alias} is L${res.jobLevel}. Please enter a valid L7+ leader.`);
                    return;
                }
                if (res.status === 'not_in_system') {
                    // CARDS is authoritative and has no such person → invalid alias → HARD BLOCK.
                    setError(`❌ ${alias} is not a valid Amazon employee alias. Please enter a valid L7+ manager.`);
                    return;
                }
                if (res.status === 'not_found') {
                    // CARDS unavailable → couldn't verify → soft warn, allow proceed (prior behavior).
                    setLeaderLookupError(`⚠️ Could not verify ${alias} right now (directory lookup unavailable). You may proceed, but ensure the leader is at or above L7, otherwise the request will be rejected.`);
                    // allow proceed
                } else if (res.status === 'error') {
                    setError('⚠️ Could not verify the leader right now. Please try again.');
                    return;
                }
            }
            setError('');
            setStep(3);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setStep(4);
        setError('');

        try {
            const requestId = `PAM-BR-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

            const response = await submitToApprovals({
                requestId,
                requesterAlias: userAlias,
                l8ManagerAlias: normalizeAlias(leaderAlias),
                templateId: '225066',
                title: `T178 Bindle Access Request — ${requestType} — ${requestId}`,
                summary: `Bindle Access Request: ${requestType}. Permission: ${permissionType}. Use case: ${useCase.substring(0, 200)}`,
                viewerUrl: `https://permissions-access-management.tse.amazon.dev/viewer?id=${requestId}`,
                formData: {
                    requestType: 'bindle_access',
                    bindleRequestType: requestType,
                    permissionType,
                    useCase,
                    paragonActions,
                    posixAccount,
                    saferAICheck,
                    saferAIId: saferAICheck === 'Yes' ? saferAIId : 'N/A',
                    userList: posixAccount || userAlias,
                    team: 'Bindle Access',
                    selectedPermissionNames: [`Bindle - ${permissionType}`],
                },
                validationSummary: { totalUsers: 1, totalEligible: 1, totalIneligible: 0, totalNotFound: 0 }
            });

            setResult(response);
            setStep(5);
        } catch (err) {
            setError(err.message || 'Failed to submit request');
            setStep(3);
        }
        setIsSubmitting(false);
    };

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '32px' }}>
                <button onClick={() => navigate('/request')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#0073bb', padding: 0, marginBottom: '12px' }}>
                    ← Back to Access Types
                </button>
                <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111', margin: '0 0 6px' }}>📦 T178 Bindle Access Request</h1>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Request bindle-level access for AI Agents, system accounts, or applications</p>
            </div>

            {/* Progress */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
                {['Details', 'Leader', 'Review & Submit'].map((label, idx) => (
                    <div key={label} style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{
                            height: '4px', borderRadius: '2px', marginBottom: '6px',
                            background: step > idx + 1 ? '#22c55e' : step === idx + 1 ? '#f59e0b' : '#e5e7eb'
                        }} />
                        <span style={{ fontSize: '11px', color: step >= idx + 1 ? '#333' : '#999', fontWeight: step === idx + 1 ? '700' : '400' }}>{label}</span>
                    </div>
                ))}
            </div>

            {error && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>
                    ❌ {error}
                </div>
            )}

            {/* Step 1: Form */}
            {step === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <FormField label="1. Is this request for an AI Agent or a system account or an application?" required>
                        <select value={requestType} onChange={e => setRequestType(e.target.value)} style={selectStyle}>
                            <option value="">— Select —</option>
                            <option value="AI Agent">AI Agent</option>
                            <option value="System Account">System Account</option>
                            <option value="Application">Application</option>
                        </select>
                    </FormField>

                    <FormField label="2. Type of permission needed - view/update" required description="This is the level of visibility on paragon for the system account/application/AI Bot">
                        <select value={permissionType} onChange={e => setPermissionType(e.target.value)} style={selectStyle}>
                            <option value="">— Select —</option>
                            <option value="View">View</option>
                            <option value="Update">Update</option>
                            <option value="View and Update">View and Update</option>
                        </select>
                    </FormField>

                    <FormField label="3. Use case details" required description="Please share the impact of this access request. What are you trying to build? What services will you be accessing in the tenant selected?">
                        <textarea value={useCase} onChange={e => setUseCase(e.target.value)} rows={4} style={inputStyle} placeholder="Describe your use case..." />
                    </FormField>

                    <FormField label="4. What actions on paragon will this be taking from paragon action library?" required>
                        <input type="text" value={paragonActions} onChange={e => setParagonActions(e.target.value)} style={inputStyle} placeholder="e.g., getCaseById, searchForCases, etc" />
                    </FormField>

                    <FormField label="5. If the request is for system account, please give the POSIX account name" required description="Steps to generate a POSIX account are briefed in summary section">
                        <input type="text" value={posixAccount} onChange={e => setPosixAccount(e.target.value)} style={inputStyle} placeholder="POSIX account name" />
                    </FormField>

                    <FormField label="6. Have you had a Safer AI check done for this automation?" required description="Refer summary section to obtain the Safer AI check">
                        <select value={saferAICheck} onChange={e => setSaferAICheck(e.target.value)} style={selectStyle}>
                            <option value="">— Select —</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                            <option value="Not Applicable">Not Applicable</option>
                        </select>
                    </FormField>

                    {saferAICheck === 'Yes' && (
                        <FormField label="7. If yes, please provide the Safer AI unique ID" required description="You can also attach the documentation for our reference">
                            <input type="text" value={saferAIId} onChange={e => setSaferAIId(e.target.value)} style={inputStyle} placeholder="Safer AI unique ID" />
                        </FormField>
                    )}

                    <button onClick={handleNext} style={primaryBtnStyle}>Continue to Leader Selection →</button>
                </div>
            )}

            {/* Step 2: Leader */}
            {step === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ padding: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>L7+ Manager Approval Required</div>
                        <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
                            Your L7+ manager will be Stage 2 approver. Stage 1 is the Bindle POC (Brian Bialke), and Stage 3 is the SPS Permissions Team.
                        </p>
                    </div>

                    <FormField label="L7+ Manager Alias" required description="Auto-detected from your reporting chain. If it's wrong or blank, edit it or re-run auto-detect.">
                        <input
                            type="text"
                            value={leaderAlias}
                            onChange={e => { setLeaderAlias(e.target.value); setLeaderAutoDetected(false); setError(''); }}
                            onBlur={e => setLeaderAlias(normalizeAlias(e.target.value))}
                            style={inputStyle}
                            placeholder="Enter L7+ manager alias"
                        />
                        <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            {isLoadingLeader && <span style={{ fontSize: '12px', color: '#666' }}>⏳ Auto-detecting...</span>}
                            {!isLoadingLeader && leaderInfo && leaderInfo.alias && (
                                <span style={{ fontSize: '12px', color: '#166534' }}>
                                    ✅ Detected: {leaderInfo.alias}{leaderInfo.jobLevel ? ` (L${leaderInfo.jobLevel})` : ''}
                                </span>
                            )}
                            {!isLoadingLeader && (
                                <button type="button" onClick={detectLeader}
                                    style={{ fontSize: '12px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                                    🔍 Re-run auto-detect
                                </button>
                            )}
                        </div>
                        {!isLoadingLeader && leaderLookupError && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>
                                ⚠️ {leaderLookupError}
                            </div>
                        )}
                    </FormField>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button onClick={() => setStep(1)} style={secondaryBtnStyle}>← Back</button>
                        <button onClick={handleNext} style={primaryBtnStyle}>Continue to Review →</button>
                    </div>
                </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ padding: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '700', margin: '0 0 16px' }}>📋 Review & Submit</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                            <div><strong>Requester:</strong> {userAlias}</div>
                            <div><strong>L7 Manager:</strong> {leaderAlias}</div>
                            <div><strong>Request Type:</strong> {requestType}</div>
                            <div><strong>Permission:</strong> {permissionType}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>Use Case:</strong> {useCase}</div>
                            <div><strong>Paragon Actions:</strong> {paragonActions}</div>
                            <div><strong>POSIX Account:</strong> {posixAccount}</div>
                            <div><strong>Safer AI Check:</strong> {saferAICheck}</div>
                            {saferAICheck === 'Yes' && <div><strong>Safer AI ID:</strong> {saferAIId}</div>}
                        </div>
                    </div>

                    <div style={{ padding: '14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', color: '#166534' }}>
                        <strong>3-Stage Approval Plan:</strong><br />
                        Stage 1: Bindle POC (Brian Bialke) → Stage 2: L7 Manager ({leaderAlias}) → Stage 3: SPS Permissions Team
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button onClick={() => setStep(2)} style={secondaryBtnStyle}>← Back</button>
                        <button onClick={handleSubmit} disabled={isSubmitting} style={primaryBtnStyle}>
                            {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Bindle Access Request'}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 4: Submitting */}
            {step === 4 && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                    <p style={{ fontSize: '16px', color: '#333' }}>Submitting your request...</p>
                </div>
            )}

            {/* Step 5: Done */}
            {step === 5 && result && (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                    <h2 style={{ fontSize: '20px', color: '#166534', marginBottom: '8px' }}>Request Submitted Successfully!</h2>
                    <p style={{ fontSize: '14px', color: '#555', marginBottom: '24px' }}>
                        Request ID: <strong>{result.requestId}</strong><br />
                        Approval ID: <strong>{result.approvalId}</strong>
                    </p>
                    {result.approvalsUrl && (
                        <a href={result.approvalsUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: '#0073bb' }}>
                            View on Approvals.amazon.com ↗
                        </a>
                    )}
                    <div style={{ marginTop: '24px' }}>
                        <button onClick={() => navigate('/request')} style={secondaryBtnStyle}>← Back to Access Types</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Reusable form field wrapper
const FormField = ({ label, required, description, children }) => (
    <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
            {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
        </label>
        {description && <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>{description}</p>}
        {children}
    </div>
);

const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box'
};

const selectStyle = {
    ...inputStyle, background: '#fff', cursor: 'pointer'
};

const primaryBtnStyle = {
    padding: '14px 24px', background: '#22c55e', color: '#fff', border: 'none',
    borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', width: '100%'
};

const secondaryBtnStyle = {
    padding: '12px 20px', background: '#f3f4f6', color: '#333', border: '1px solid #e5e7eb',
    borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
};

export default BindleRequest;
